import { msg, t } from '@lingui/macro';

import config from 'config';

import * as svars from 'assets/style/variables';

import { cosineInterpolation, getInterpolatedColor } from './colors';

export const PRIORITIZATION_CATEGORY_ALL = 'all';
const PRIORITIZATION_CATEGORY_IMPROVE = 'improve';
const PRIORITIZATION_CATEGORY_PROMOTE = 'promote';
const PRIORITIZATION_CATEGORY_MONITOR = 'monitor';
const PRIORITIZATION_CATEGORY_AMPLIFY = 'amplify';

export const priorityCategoryMap = {
  [PRIORITIZATION_CATEGORY_IMPROVE]: {
    label: msg({ id: 'improve' }),
    color: svars.priorityImproveColor,
    background: svars.priorityImproveColorLightest,
    computeLabelPosition: ({ xMin, yMin, labelHeight, labelWidth }) => ({
      x1: xMin,
      x2: xMin + labelWidth,
      y1: yMin + labelHeight,
      y2: yMin,
    }),
  },
  [PRIORITIZATION_CATEGORY_PROMOTE]: {
    label: msg({ id: 'promote' }),
    color: svars.priorityPromoteColor,
    background: svars.priorityPromoteColorLightest,
    computeLabelPosition: ({ xMax, yMax, labelHeight, labelWidth }) => ({
      x1: xMax - labelWidth,
      x2: xMax,
      y1: yMax,
      y2: yMax - labelHeight,
    }),
  },
  [PRIORITIZATION_CATEGORY_MONITOR]: {
    label: msg({ id: 'monitor' }),
    color: svars.priorityMonitorColor,
    background: svars.priorityMonitorColorLightest,
    computeLabelPosition: ({ xMin, yMax, labelHeight, labelWidth }) => ({
      x1: xMin,
      x2: xMin + labelWidth,
      y1: yMax - labelHeight,
      y2: yMax,
    }),
  },
  [PRIORITIZATION_CATEGORY_AMPLIFY]: {
    label: msg({ id: 'amplify' }),
    color: svars.priorityAmplifyColor,
    background: svars.priorityAmplifyColorLightest,
    computeLabelPosition: ({ xMax, yMin, labelHeight, labelWidth }) => ({
      x1: xMax - labelWidth,
      x2: xMax,
      y1: yMin + labelHeight,
      y2: yMin,
    }),
  },
};

const PRIORITY_CATEGORY_ALL_ITEM = {
  key: PRIORITIZATION_CATEGORY_ALL,
  i18nLabel: msg({ id: 'all' }),
  value: PRIORITIZATION_CATEGORY_ALL,
};

export const PRIORITIZATION_CATEGORY_ITEMS = [
  PRIORITY_CATEGORY_ALL_ITEM,
  ...Object.entries(priorityCategoryMap).map(([key, { label }]) => ({
    key,
    i18nLabel: label,
    value: key,
  })),
];

export const getPriorityCategory = (item) => {
  if (item.average_score > 50 && item.average_sentiment > 0) {
    return 'promote';
  }
  if (item.average_score < 50 && item.average_sentiment < 0) {
    return 'improve';
  }
  if (item.average_score > 50 && item.average_sentiment < 0) {
    return 'monitor';
  }
  if (item.average_score < 50 && item.average_sentiment > 0) {
    return 'amplify';
  }
  return 'monitor';
};

const PRIORITY_DOMAIN = [0, 1];

// eslint-disable-next-line import/prefer-default-export
export const getPriorityCategoryColor = (sentiment, reviewScore) =>
  priorityCategoryMap[
    getPriorityCategory({
      average_sentiment: sentiment,
      average_score: reviewScore,
    })
  ]?.color;

export const getPriorityCategoryInterpolatedColor = (item, domain) => {
  const priorityCategory = getPriorityCategory(item);
  const xCenter = (config.SENTIMENT_DOMAIN[0] + config.SENTIMENT_DOMAIN[1]) / 2;
  const yCenter =
    (config.REVIEW_SCORE_DOMAIN[0] + config.REVIEW_SCORE_DOMAIN[1]) / 2;
  const distanceToCenter = Math.sqrt(
    (item.average_sentiment - xCenter) ** 2 / (domain.x[1] - domain.x[0]) +
      ((item.average_score - yCenter) / (domain.y[1] - domain.y[0])) ** 2
  );

  switch (priorityCategory) {
    case 'promote':
      return getInterpolatedColor(
        distanceToCenter,
        cosineInterpolation,
        svars.colorWhite,
        svars.priorityPromoteColorLightest,
        svars.priorityPromoteColor,
        PRIORITY_DOMAIN
      );
    case 'improve':
      return getInterpolatedColor(
        distanceToCenter,
        cosineInterpolation,
        svars.colorWhite,
        svars.priorityImproveColorLightest,
        svars.priorityImproveColor,
        PRIORITY_DOMAIN
      );
    case 'monitor':
      return getInterpolatedColor(
        distanceToCenter,
        cosineInterpolation,
        svars.priorityMonitorColorLightest,
        svars.priorityMonitorColorLightest,
        svars.priorityMonitorColor,
        PRIORITY_DOMAIN
      );
    case 'amplify':
      return getInterpolatedColor(
        distanceToCenter,
        cosineInterpolation,
        svars.priorityAmplifyColorLightest,
        svars.priorityAmplifyColorLightest,
        svars.priorityAmplifyColor,
        PRIORITY_DOMAIN
      );
    default:
      return null;
  }
};

// Sentiment

export const SENTIMENT_POLARITY_ITEMS = [
  {
    key: 'positives',
    i18nLabel: msg({ id: 'positive-sentiment' }),
    value: 'positives',
  },
  {
    key: 'negatives',
    i18nLabel: msg({ id: 'negative-sentiment' }),
    value: 'negatives',
  },
  // We don't propose neutral filter to avoid showing only neutral chunks
  // { key: 'neutrals', i18nLabel: msg({ id: "neutral-sentiment" }), value: 'neutrals' },
];

export const SENTIMENT_POLARITY_ITEMS_ALL = [
  PRIORITY_CATEGORY_ALL_ITEM,
  ...SENTIMENT_POLARITY_ITEMS,
];

export const getCampaignLanguages = (campaignTranslation = false) => {
  const languages = [
    { key: 'en', value: 'en', text: t({ id: 'english' }), flag: 'us' },
    { key: 'fr', value: 'fr', text: t({ id: 'french' }), flag: 'fr' },
    {
      key: 'zh',
      value: 'zh',
      text: t({ id: 'simplified-chinese' }),
      flag: 'cn',
    },
  ];
  if (campaignTranslation) {
    languages.push({
      key: 'ar',
      value: 'ar',
      text: t({ id: 'arabic' }),
      flag: 'sa',
    });
  }
  return languages;
};

export const isRtlLanguage = (lang) => {
  const rtlLanguages = ['ar', 'he'];
  return rtlLanguages.includes(lang);
};
